import { getColor } from "@/lib/methodUtils";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { memo } from "react";
import { ButtonLoader } from "../Loader/ButtonLoader";
import styles from "./Modal.module.scss";

interface ModalProps {
  modalOpen: boolean;
  closeModal: () => void;
  heading?: string;
  children: React.ReactNode;
  width?: string;
  saveButtonText?: string;
  onSave?: () => void;
  errorMessage?: string;
  smallHeading?: string;
  hideFooter?: boolean;
  hideAddButton?: boolean;
  isDisabled?: boolean;
  isColorBadge?: boolean;
  color?: string;
  showGreenButton?: boolean;
  greenText?: string;
  onGreen?: () => void;
  showRedButton?: boolean;
  redText?: string;
  onRed?: () => void;
  rightHeadingText?: string;
  isLoading?: boolean;
  contentSx?: object;
  cancelButtonText?: string;
  height?: string;
  hideCancelButton?: boolean;
  isSendEmail?: boolean;
  SendEmail?: () => void;
  SendEmailText?: string;
  isEmailLoading?: boolean;
  isRightHeadingButton?: boolean;
  rightHeadingButtonText?: string;
  onRightHeadingButtonClick?: () => void;
  timeError?: string;
  subHeading?: string;
  hideHeading?: boolean;
  dense?: boolean;
  isCloseClicked?: boolean;
  isButtonVariant?: boolean;
  smallHeadingCSS?: string;
  subHeadingCSS?: string;
  footerContent?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  modalOpen,
  closeModal,
  heading,
  children,
  width,
  saveButtonText = "Save",
  onSave,
  errorMessage,
  smallHeading,
  hideFooter,
  hideAddButton,
  isDisabled,
  isColorBadge,
  color,
  cancelButtonText = "Cancel",
  height,
  hideCancelButton,
  showGreenButton,
  greenText,
  onGreen,
  showRedButton,
  redText,
  onRed,
  isLoading = false,
  rightHeadingText,
  contentSx,
  isSendEmail,
  SendEmail,
  SendEmailText = "Send Email",
  isEmailLoading = false,
  isRightHeadingButton = false,
  rightHeadingButtonText,
  onRightHeadingButtonClick,
  timeError,
  subHeading,
  hideHeading = false,
  dense = false,
  isCloseClicked = true,
  isButtonVariant = false,
  smallHeadingCSS,
  subHeadingCSS,
  footerContent,
}: ModalProps) => {
  return (
    <Dialog
      open={modalOpen}
      onClose={isCloseClicked ? closeModal : undefined}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          style: {
            width: width || "40vw",
            height: height || "auto",
            maxWidth: "70vw",
            maxHeight: "80vh"
          },
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid #e9edf5",
            boxShadow: "0 10px 30px rgba(38, 46, 77, 0.12)",
          },
        },
      }}
    >
      {!hideHeading && (
        <DialogTitle
          sx={{
            px: 2.5,
            py: dense ? 1.25 : 1.75,
            borderBottom: "1px solid #edf0f7",
            background: "linear-gradient(180deg, #fafbfe 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          <div className={styles.modalHeader}>
            <div className={styles.headerTitleWrapper}>
              <div className={styles.titleRow}>
                <span className={styles.mainTitle}>{heading}</span>

                {smallHeading && (
                  <small className={smallHeadingCSS ? `ml-2 inline-block ${smallHeadingCSS}` : "ml-2 text-gray-500 font-sm"}>
                    {smallHeading}
                  </small>
                )}
              </div>

              {subHeading && (
                <div className={subHeadingCSS ? `mt-1.5 w-fit inline-block ${subHeadingCSS}` : "text-gray-500 font-sm"}>
                  {subHeadingCSS ? subHeading : <small>{subHeading}</small>}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {rightHeadingText && (
                <span className={styles.rightHeading}>{rightHeadingText}</span>
              )}
              {isRightHeadingButton && (
                <Button
                  variant={isButtonVariant ? "text" : "contained"}
                  size={dense ? "small" : "medium"}
                  onClick={onRightHeadingButtonClick}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: "12px",
                    borderRadius: "8px",
                  }}
                >
                  {rightHeadingButtonText || "Add More"}
                </Button>
              )}
            </div>
          </div>

          {isColorBadge && (
            <div
              className={styles.colorBadge}
              style={{ backgroundColor: getColor(color) }}
            />
          )}
        </DialogTitle>
      )}
      <DialogContent
        dividers
        sx={{
          p: dense ? 2 : 2.5,
          ...contentSx,
        }}
      >
        {children}
        {errorMessage && (
          <Typography color="error" className={styles.errorMessage}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      {/* Footer */}
      {!hideFooter && (
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #edf0f7",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {/* LEFT SIDE */}
          <div className="flex flex-wrap items-center gap-2">
            {footerContent}
          </div>

          {/* RIGHT SIDE */}
          <div className="flex flex-wrap items-center gap-2">
            {/* show error */}
            {timeError && <p className={styles.timeError}>{timeError}</p>}

            {showGreenButton && (
              <Button
                variant="contained"
                size={dense ? "small" : "medium"}
                onClick={onGreen || closeModal}
                sx={{
                  backgroundColor: "#10b981",
                  boxShadow: "0 4px 14px rgba(16, 185, 129, 0.28)",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  borderRadius: "8px",
                  px: 2.5,
                  height: "36px",
                  "&:hover": {
                    backgroundColor: "#059669",
                    boxShadow: "0 6px 18px rgba(16, 185, 129, 0.38)",
                  },
                }}
              >
                {greenText || "Accept"}
              </Button>
            )}

            {showRedButton && (
              <Button
                variant="contained"
                size={dense ? "small" : "medium"}
                onClick={onRed || closeModal}
                sx={{
                  backgroundColor: "#ef4444",
                  boxShadow: "0 4px 14px rgba(239, 68, 68, 0.28)",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  borderRadius: "8px",
                  px: 2,
                  height: "36px",
                  "&:hover": {
                    backgroundColor: "#dc2626",
                    boxShadow: "0 4px 14px rgba(239, 68, 68, 0.38)",
                  },
                }}
              >
                {redText || "Reject"}
              </Button>
            )}

            {!hideCancelButton && (
              <Button
                variant="text"
                size={dense ? "small" : "medium"}
                onClick={closeModal}
                sx={{
                  color: "#64748b",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  borderRadius: "8px",
                  px: 2,
                  height: "36px",
                  "&:hover": { backgroundColor: "#f1f5f9" },
                }}
              >
                {cancelButtonText}
              </Button>
            )}

            {!hideAddButton && (
              <ButtonLoader
                loading={isLoading}
                variant="contained"
                size={dense ? "small" : "medium"}
                disabled={isDisabled ? true : false}
                onClick={onSave || closeModal}
                sx={{
                  backgroundColor: "#5d78ff",
                  boxShadow: "0 4px 14px rgba(93, 120, 255, 0.28)",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  borderRadius: "8px",
                  px: 2.5,
                  height: "36px",
                  "&:hover": {
                    backgroundColor: "#4863e6",
                    boxShadow: "0 6px 18px rgba(93, 120, 255, 0.38)",
                  },
                }}
              >
                {saveButtonText}
              </ButtonLoader>
            )}

            {isSendEmail && (
              <ButtonLoader
                loading={isEmailLoading}
                variant="outlined"
                size={dense ? "small" : "medium"}
                onClick={SendEmail}
                spinnerColor="#808080"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  fontFamily: "Poppins",
                  borderRadius: "8px",
                  px: 2,
                  height: "36px",
                }}
              >
                {SendEmailText || "Send Email"}
              </ButtonLoader>
            )}
          </div>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(Modal);
